import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { CreateSubscriptionInput, CustomerRowDto, EndDatePreviewDto, SubscriptionCreatedDto, SubscriptionMealRules } from '@tiffinify/contracts';
import { addDays, daysLabel, formatDay, formatPhone, formatShort, mealRulesToSave, mealsOf, sameDays, type Food, type Slot } from '@tiffinify/core';
import { BtnSecondary, Callout, Card, Chip, Chips, DayPicker, ErrorText, Field, Helper, MealPicker, PageLoader, RadioCard, Section, Segmented, TiffinMini, ToggleRow, useConfirm, LoadError } from '../components/ui';
import { Amt, Av, Back, Badge as PBadge, Btn, Card as PCard, Ic, KV, Kick, Note, Opt, Row, Screen, Sm, Sp, Title, Top } from '../components/p';
import { MealChoiceSection } from '../components/MealChoice';
import { ApiClientError, del, post, put } from '../lib/api';
import { errorMessage, formatInr, localPhone, mealsLabel, planUnit } from '../lib/format';
import { useCustomers, useMe, useProfile, useSettings } from '../lib/queries';

type Mode = 'new' | 'renew' | 'edit';

export function SubscriptionForm({ mode }: { mode: Mode }) {
  const { confirm, sheet: confirmSheet } = useConfirm();
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe().data;
  const today = me?.today ?? '';
  const settings = useSettings();
  const profile = useProfile(id);
  const p = mode === 'new' ? undefined : profile.data;

  // customer
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [area, setArea] = useState('');
  const [addingArea, setAddingArea] = useState(false);
  const [food, setFood] = useState<Food>('veg');
  const [notes, setNotes] = useState('');
  // plan
  const [planId, setPlanId] = useState<string>('custom');
  const [planName, setPlanName] = useState('');
  const [slot, setSlot] = useState<Slot>('lunch');
  const [mealDays, setMealDays] = useState('26');
  // The plan's own weekdays; null follows the kitchen's delivery days.
  const [ownDays, setOwnDays] = useState<number[] | null>(null);
  const [startDate, setStartDate] = useState('');
  const [price, setPrice] = useState('');
  const [mealRules, setMealRules] = useState<SubscriptionMealRules>({});
  // payment
  const [paid, setPaid] = useState('');
  const [method, setMethod] = useState<'upi' | 'cash'>('upi');
  const [welcome, setWelcome] = useState(true);
  const [fields, setFields] = useState<Record<string, string>>({});
  // A new subscription is three steps (who, plan, meals), kept in the address so the phone's back button steps back.
  const [params, setParams] = useSearchParams();
  // Step 1 can pick someone who already orders: their details come along and the plan is added to them.
  const [find, setFind] = useState('');
  const [known, setKnown] = useState<CustomerRowDto | null>(null);
  const found = useCustomers('all', find.trim(), mode === 'new' && find.trim().length >= 2);
  const knownProfile = useProfile(known?.subscriberId ?? '');
  // Renew shows the plan carried over; Change opens the choices.
  const [changing, setChanging] = useState(false);

  // Fill from the existing customer (renew / edit) once, and the start date for a new plan.
  // A layout effect, so the filled form is the first thing painted and nothing shifts.
  const [filled, setFilled] = useState(false);
  useLayoutEffect(() => {
    if (filled) return;
    if (mode === 'new') { if (today) { setStartDate(today); setFilled(true); } return; }
    if (!p) return;
    setName(p.subscriber.name); setPhone(localPhone(p.subscriber.phone)); setEmail(p.subscriber.email ?? ''); setArea(p.subscriber.area); setFood(p.subscriber.food); setNotes(p.subscriber.notes);
    const from = mode === 'renew' ? p.renewal : p.subscription;
    if (from) {
      setPlanName(from.planName); setSlot(from.slot); setMealDays(String(from.mealDays)); setPrice(String(from.price)); setOwnDays(from.days);
      setStartDate(from.startDate);
      const rules = from.mealRules ?? (mode === 'renew' ? p.subscription?.mealRules : null);
      if (rules) setMealRules(rules);
    }
    setFilled(true);
  }, [filled, mode, p, today]);

  useEffect(() => {
    const k = knownProfile.data;
    if (!known || !k || k.subscriber.id !== known.subscriberId) return;
    setEmail(k.subscriber.email ?? ''); setFood(k.subscriber.food); setNotes(k.subscriber.notes);
    const rules = k.renewal?.mealRules ?? k.subscription?.mealRules;
    if (rules) setMealRules(rules);
  }, [known, knownProfile.data]);
  const pickKnown = (c: CustomerRowDto) => { setKnown(c); setName(c.name); setPhone(localPhone(c.phone)); setArea(c.area); setFind(''); };
  const forget = () => { setKnown(null); setName(''); setPhone(''); setEmail(''); setArea(''); setFood('veg'); setNotes(''); setMealRules({}); };
  const existingId = mode === 'renew' ? id : known?.subscriberId;

  const md = Number(mealDays);
  const kitchenDays = settings.data?.kitchen.deliveryDays ?? [1, 2, 3, 4, 5, 6];
  const days = ownDays ?? kitchenDays;
  // Sent as null when they are the kitchen's days, so the plan keeps following the kitchen.
  const daysToSend = sameDays(days, kitchenDays) ? null : days;
  const pr = Number(price.replace(/[^\d]/g, ''));
  const paidN = Number(paid.replace(/[^\d]/g, ''));
  const validPlan = md >= 1 && md <= 365 && /^\d{4}-\d{2}-\d{2}$/.test(startDate);

  const preview = useQuery({
    queryKey: ['end-preview', startDate, md, slot, daysToSend?.join(',') ?? 'kitchen', existingId],
    queryFn: () => post<EndDatePreviewDto>('/subscriptions/preview', { startDate, mealDays: md, slot, days: daysToSend, ...(existingId ? { subscriberId: existingId } : {}) }),
    enabled: mode !== 'edit' && validPlan,
    placeholderData: (prev) => prev,
  });

  const plans = settings.data?.plans ?? [];
  // Meals the kitchen serves (an existing plan's other meals still show, so it can be edited).
  const served = mealsOf(settings.data?.kitchen.meals ?? 'breakfast+lunch+dinner');
  const areas = settings.data?.areas ?? [];
  const matchingPlans = plans.filter((pl) => pl.slot === slot);
  const otherPlans = plans.filter((pl) => pl.slot !== slot);
  // A renewal or edit that matches one of the kitchen's plans shows that plan picked, not Custom.
  const selectedPlan = planId !== 'custom' ? planId
    : plans.find((pl) => pl.slot === slot && pl.name === planName && pl.mealDays === md && pl.price === pr)?.id ?? 'custom';

  const pickPlan = (planIdx: string) => {
    setPlanId(planIdx);
    const pl = plans.find((x) => x.id === planIdx);
    if (pl) { setPlanName(pl.name); setSlot(pl.slot); setMealDays(String(pl.mealDays)); setPrice(String(pl.price)); setOwnDays(pl.days); }
  };
  const pickSlot = (newSlot: Slot) => {
    setSlot(newSlot);
    const match = plans.find((x) => x.slot === newSlot);
    if (match) pickPlan(match.id);
    else setPlanId('custom');
  };

  // A new subscription starts from the kitchen's first plan, so price and days are never blank.
  const [autoPlan, setAutoPlan] = useState(false);
  useLayoutEffect(() => {
    if (mode === 'new' && !autoPlan && plans[0]) { pickPlan(plans[0].id); setAutoPlan(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, autoPlan, plans.length]);
  const effectivePlanName = planId !== 'custom' || planName ? planName : `${mealsLabel(slot)} · ${md} days`;

  const shift = preview.data?.shift;
  const isMidCycle = Boolean(shift?.isMidCycle);
  const credit = shift?.credit ?? 0;
  // Only as much credit as the new price takes moves across (the server does the same).
  const applied = Math.min(credit, pr);
  const netDue = Math.max(0, pr - applied - paidN);

  const save = useMutation({
    mutationFn: async () => {
      setFields({});
      const subscriber = { name, phone, email: email.trim() || null, area, food, notes };
      const rules = mealRulesToSave(mealsOf(slot), mealRules, food);
      if (mode === 'edit') {
        await put(`/subscribers/${id}`, subscriber);
        const s = p?.subscription;
        if (s && !s.closedOn) {
          await put(`/subscriptions/${s.id}`, { price: pr, mealDays: md, startDate, planName: planName || effectivePlanName, slot, days: daysToSend, mealRules: rules ?? null });
        }
        return null;
      }
      const body: CreateSubscriptionInput = {
        planName: effectivePlanName, slot, mealDays: md, price: pr, startDate, days: daysToSend,
        ...(planId !== 'custom' ? { planId } : {}),
        ...(existingId ? { subscriberId: existingId } : { subscriber }),
        ...(paidN > 0 ? { firstPayment: { amount: paidN, method } } : {}),
        mealRules: rules,
      };
      return post<SubscriptionCreatedDto>('/subscriptions', body);
    },
    onSuccess: (res) => {
      void qc.invalidateQueries();
      if (!res) return nav(`/s/${id}`, { state: { flash: { text: 'Details saved.' } } });
      const first = name.split(' ')[0] ?? name;
      const text = res.shift?.isMidCycle
        ? `Switched plans${res.shift.creditApplied ? `, with ${formatInr(res.shift.creditApplied)} carried over` : ''}. Ends ${formatDay(res.endDate)}.`
        : `${mode === 'renew' ? 'Renewed' : 'Subscription created'}. Ends ${formatDay(res.endDate)}.`;
      nav(`/s/${res.subscriberId}`, {
        state: { flash: { text, ...(welcome ? { wa: { label: mode === 'renew' ? `Send to ${first}` : `Welcome ${first}`, link: res.welcome.link } } : {}) } },
      });
    },
    onError: (e) => { if (e instanceof ApiClientError) setFields(e.fields); },
  });

  const delCustomer = useMutation({
    mutationFn: () => del<{ ok: boolean }>(`/subscribers/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries();
      nav('/subscribers', { replace: true, state: { flash: { text: `${name} and all their records were deleted.` } } });
    },
  });

  const isClosedSub = mode === 'edit' && Boolean(p?.subscription?.closedOn);
  const contactOk = name.trim() !== '' && phone.replace(/\D/g, '').length >= 10;
  // A blank price would save a ₹0 plan without anyone noticing: it has to be typed, even if 0.
  const planOk = isClosedSub || (validPlan && price.trim() !== '');
  const canSave = !save.isPending && contactOk && planOk;
  const back = mode === 'new' ? '/subscribers' : `/s/${id}`;
  const was = p?.subscription;
  const first = name.trim().split(/\s+/)[0] || 'the customer';
  const asked = Number(params.get('step')) || 1;
  const step = mode !== 'new' ? 0 : !contactOk ? 1 : asked >= 3 && planOk ? 3 : asked >= 2 ? 2 : 1;
  const goStep = (n: number) => setParams({ step: String(n) });
  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const head = mode === 'new' ? (
    <Top>
      {step === 1 ? <Back to={back} label="Cancel" /> : <Back onClick={() => nav(-1)} label="Back" />}
      <Sp /><Kick>Step {step} of 3</Kick>
    </Top>
  ) : <Top><Back to={back} label={p ? first : 'Back'} /></Top>;
  const heading = mode === 'new'
    ? (step === 1 ? <Title h="Who is it for?" />
      : step === 2 ? <Title h="Choose the plan" sub={`For ${[name.trim(), formatPhone(phone), area].filter(Boolean).join(' · ')}`} />
      : <Title h="Meals and send" sub={`For ${first} · ${mealsLabel(slot).toLowerCase()} · ${md} ${planUnit(slot, md)}`} />)
    : mode === 'renew' ? <Title h="Renew" sub={`${name} · carried over from the current plan.`} />
    : <Title h="Edit details" sub={name} />;

  if (settings.isError || (mode !== 'new' && profile.isError)) {
    return <Screen gap={16}>{head}{heading}<LoadError what="the form" onRetry={() => { void settings.refetch(); void profile.refetch(); }} /></Screen>;
  }
  // Plans and areas come with the settings: wait for them so the form does not jump when they arrive.
  if (settings.isPending || (mode !== 'new' && profile.isPending)) {
    return <Screen gap={16}>{head}{mode === 'new' ? heading : null}<PageLoader /></Screen>;
  }

  const end = preview.data?.endDate;
  const customer = (
    <>
      <Field label="Name" id="f-name" value={name} onChange={setName} autoComplete="off" placeholder="Anjali Krishnan" error={fields.name ?? fields['subscriber.name']} />
      <Field label="WhatsApp number" id="f-phone" value={phone} onChange={setPhone} prefix="+91" inputMode="tel" placeholder="98765 43210" error={fields.phone ?? fields['subscriber.phone']} />
      <div className="stack">
        <span className="label">Delivery location</span>
        <Chips label="Delivery location">
          {areas.map((a) => <Chip key={a.id} on={area === a.name} onClick={() => setArea(area === a.name ? '' : a.name)}>{a.name}</Chip>)}
          {area && !areas.some((a) => a.name === area) && <Chip on onClick={() => setArea('')}>{area}</Chip>}
          <Chip icon="plus" dashed onClick={() => setAddingArea(true)}>Add</Chip>
        </Chips>
        {addingArea && <Field label="New location" id="f-area" value={area} onChange={setArea} placeholder="e.g. Jayanagar" />}
      </div>
      <Field label={mode === 'new' ? 'Email (optional, for receipts)' : 'Email'} id="f-email" type="email" value={email} onChange={setEmail} autoComplete="email" placeholder={mode === 'new' ? 'name@example.com' : 'Optional'} error={fields.email ?? fields['subscriber.email']} />
    </>
  );

  // The kitchen's plans as cards, then "Your own" with the meals, days, length and price to set.
  const planChoices = (
    <>
      {mode === 'renew' && was && was.slot !== slot && <Callout icon="refresh" title="Switching meals">{mealsLabel(was.slot)} → {mealsLabel(slot)}</Callout>}
      {plans.map((pl) => (
        <Opt key={pl.id} t={pl.name} on={selectedPlan === pl.id} onClick={() => pickPlan(pl.id)} right={<Amt>{formatInr(pl.price)}</Amt>}
          s={[pl.name.includes(String(pl.mealDays)) ? null : `${mealsLabel(pl.slot)} · ${pl.mealDays} meal days`, daysLabel(pl.days ?? kitchenDays)].filter(Boolean).join(' · ')} />
      ))}
      <Opt t="Your own" s="Pick meals, days and how long" right={<PBadge>Custom</PBadge>} on={selectedPlan === 'custom'} onClick={() => setPlanId('custom')} />
      {selectedPlan === 'custom' && (
        <Card pad>
          <div className="stack">
            <span className="label">Meals each day</span>
            <MealPicker label="Meals each day" value={slot} allowed={served} onChange={(v) => { setSlot(v); setPlanId('custom'); }} />
          </div>
          <Field label="Plan name" id="f-plan-name" value={planName} onChange={(v) => { setPlanName(v); setPlanId('custom'); }} placeholder={`${mealsLabel(slot)} · ${md} days`} error={fields.planName} />
          <div className="stack">
            <span className="label">Delivery days</span>
            <DayPicker label="Delivery days" value={days} onChange={(v) => { setOwnDays(v); setPlanId('custom'); }} />
          </div>
          <div className="two">
            <Field label="Meal days" id="f-days" value={mealDays} onChange={(v) => { setMealDays(v.replace(/\D/g, '')); setPlanId('custom'); }} inputMode="numeric" error={fields.mealDays} />
            <Field label="Price" id="f-price" value={price} onChange={(v) => { setPrice(v.replace(/[^\d]/g, '')); setPlanId('custom'); }} prefix="₹" inputMode="numeric" error={fields.price} />
          </div>
          <Helper>Only these days count as meal days{days.length < 7 ? ` (${daysLabel(days)})` : ''}. Holidays are skipped.</Helper>
        </Card>
      )}
    </>
  );

  const shiftNote = isMidCycle && shift && (
    <Callout icon="refresh" title={`${shift.currentPlanName} stops after ${formatDay(shift.closedOn)}`}>
      {shift.consumedMeals} of {shift.totalMeals} meals used ({formatInr(shift.proratedCost)}).{' '}
      {credit === 0 ? 'Nothing left over to carry across.'
        : credit > pr ? `${formatInr(applied)} of the ${formatInr(credit)} left pays this plan; ${formatInr(credit - applied)} stays on the old plan to refund.`
        : `The ${formatInr(credit)} left carries across to this plan.`}
    </Callout>
  );

  const payment = (
    <>
      {applied > 0 && <div className="kv credit-row"><span>Carried over from the last plan</span><span>+{formatInr(applied)}</span></div>}
      <Field
        label={applied > 0 ? 'Paid now, on top (optional)' : 'Paid now (optional)'}
        id="f-paid"
        value={paid}
        onChange={(v) => setPaid(v.replace(/[^\d]/g, ''))}
        prefix="₹"
        inputMode="numeric"
        placeholder="0 · UPI or cash"
        hint={paidN > pr - applied && pr > 0 ? `That is more than the ${formatInr(pr - applied)} left to pay.` : paidN > 0 || applied > 0 ? `Still to pay: ${formatInr(netDue)}` : 'Leave empty if nothing is paid yet'}
        error={fields['firstPayment.amount']}
      />
      {paidN > 0 && <div className="stack"><span className="label">Paid by</span><Segmented label="Paid by" value={method} onChange={setMethod} options={[{ value: 'upi', label: 'UPI' }, { value: 'cash', label: 'Cash' }]} /></div>}
    </>
  );

  const meals = !isClosedSub && (
    <MealChoiceSection
      slot={slot}
      mealRules={mealRules}
      onChangeMealRules={setMealRules}
      dishes={settings.data?.dishes ?? []}
      customerFood={food}
      onChangeCustomerFood={setFood}
      notes={notes}
      onChangeNotes={setNotes}
    />
  );

  const summary = (
    <PCard pad style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <KV k="Plan" v={`${mealsLabel(slot)} · ${md} ${planUnit(slot, md)} · ${daysLabel(days)}`} />
      <KV k="Dates" v={<span className="p-pair">{startDate ? formatDay(startDate) : '—'} – {end ? formatDay(end) : '—'}{preview.isFetching && <span className="hero-busy" aria-label="Recalculating"><TiffinMini /></span>}</span>} />
      <KV k="Price" v={formatInr(pr)} />
      {applied > 0 && <KV k="Carried over" v={`+${formatInr(applied)}`} />}
      <KV k="Paid now" v={formatInr(paidN)} />
      <KV k="To pay" v={formatInr(netDue)} />
    </PCard>
  );

  const welcomeToggle = (
    <Card className="toggle-card">
      <ToggleRow
        title={mode === 'renew' ? 'Send a renewal message' : 'Send a welcome message'}
        sub={mode === 'renew' ? 'On WhatsApp, with the new dates and meals' : 'On WhatsApp, with the plan, the end date and their My plan link'}
        on={welcome}
        onChange={setWelcome}
      />
    </Card>
  );
  const saveError = <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>;

  if (mode === 'new') {
    return (
      <Screen gap={16}>
        {head}
        {heading}
        {step === 1 && (
          <>
            {known ? (
              <>
                <PCard>
                  <Row left={<Av name={known.name} />} t={known.name} s={[formatPhone(known.phone), known.area].filter(Boolean).join(' · ')} right={<Sm label="Change" onClick={forget} />} />
                </PCard>
                <Note icon="sparkle">{first} already orders from you, so their details and usual meal come along.</Note>
              </>
            ) : (
              <>
                <Field label="Find someone you know" id="f-find" value={find} onChange={setFind} autoComplete="off" placeholder="Name, phone or flat" />
                {find.trim().length >= 2 && found.data && (found.data.items.length > 0 ? (
                  <PCard>
                    {found.data.items.slice(0, 5).map((c) => (c.onPlan
                      ? <Row key={c.subscriberId} to={`/s/${c.subscriberId}/renew`} left={<Av name={c.name} />} t={c.name} s="Already on a plan · renew it instead" chev />
                      : <Row key={c.subscriberId} onClick={() => pickKnown(c)} left={<Av name={c.name} />} t={c.name} s={[formatPhone(c.phone), c.area].filter(Boolean).join(' · ')} chev />))}
                  </PCard>
                ) : <span className="p-status">No one by that name yet. Add them below.</span>)}
                <Kick>Or add someone new</Kick>
                {customer}
              </>
            )}
            <Btn label="Next: choose the plan" disabled={!contactOk} onClick={() => goStep(2)} />
          </>
        )}
        {step === 2 && (
          <>
            {planChoices}
            <Field label="Starts" id="f-start" type="date" value={startDate} onChange={setStartDate} error={fields.startDate} hint={end ? `Ends ${formatDay(end)}. Pauses and holidays move it on their own.` : undefined} />
            {shiftNote}
            {payment}
            <Btn label="Next: meal choices" disabled={!planOk} onClick={() => goStep(3)} />
          </>
        )}
        {step === 3 && (
          <>
            {meals}
            {summary}
            <Note icon="chat">The welcome message carries {first}’s My plan link, so they can pay and ask to pause on their own.</Note>
            {welcomeToggle}
            {saveError}
            <Btn busy={save.isPending} label={welcome ? 'Save · send welcome on WhatsApp' : 'Save subscription'} go={welcome ? 'send' : 'check'} onClick={() => save.mutate()} disabled={!canSave} />
          </>
        )}
      </Screen>
    );
  }

  if (mode === 'renew') {
    return (
      <Screen gap={16}>
        {head}
        {heading}
        <PCard>
          <Row left={<Ic icon="refresh" tone="s" />} t={effectivePlanName} s={`${daysLabel(days)} · ${md} meal days · ${Object.keys(mealRules).length ? 'same meal choices' : 'chef’s special'}`}
            right={<Sm label={changing ? 'Done' : 'Change'} onClick={() => setChanging(!changing)} />} />
          <Row left={<Ic text="₹" tone="g" />} t="Price" right={<Amt>{formatInr(pr)}</Amt>} />
        </PCard>
        {changing && <>{planChoices}{meals}</>}
        {p?.renewal && (
          <div className="stack">
            <span className="label">Starts</span>
            <Chips label="Starts">
              <Chip on={startDate === p.renewal.startDate} onClick={() => setStartDate(p.renewal!.startDate)}>After the current plan ({formatShort(p.renewal.startDate)})</Chip>
              {today && today !== p.renewal.startDate && <Chip on={startDate === today} onClick={() => setStartDate(today)}>Today ({formatShort(today)})</Chip>}
              {today && addDays(today, 1) < p.renewal.startDate && (
                <Chip on={startDate === addDays(today, 1)} onClick={() => setStartDate(addDays(today, 1))}>Tomorrow ({formatShort(addDays(today, 1))})</Chip>
              )}
            </Chips>
          </div>
        )}
        {!p?.renewal && <Field label="Starts" id="f-start" type="date" value={startDate} onChange={setStartDate} error={fields.startDate} />}
        {shiftNote}
        {payment}
        {summary}
        {welcomeToggle}
        {saveError}
        <Btn busy={save.isPending} label={isMidCycle ? 'Switch and renew' : welcome ? 'Renew · send on WhatsApp' : 'Renew plan'} go={welcome ? 'send' : 'check'} onClick={() => save.mutate()} disabled={!canSave} />
      </Screen>
    );
  }

  return (
    <Screen gap={16}>
      {head}
      {heading}
      <Section title="Customer">
        <Card pad>{customer}</Card>
      </Section>

      <Section title="Plan">
        <Card pad>
          {isClosedSub ? (
            <Helper icon="info">This plan is closed, so only the customer’s details can change. To start a new plan, use Renew on their profile.</Helper>
          ) : (
            <>
              <div className="stack">
                <span className="label">Meals each day</span>
                <MealPicker label="Meals each day" value={slot} allowed={served} onChange={pickSlot} />
              </div>
              {plans.length > 0 && (
                <div className="stack">
                  <span className="label">{matchingPlans.length > 0 ? `Plans for ${mealsLabel(slot).toLowerCase()}` : 'Plan'}</span>
                  {matchingPlans.map((pl) => (
                    <RadioCard key={pl.id} title={pl.name} sub={[`${pl.mealDays} meal days`, pl.days ? daysLabel(pl.days) : null].filter(Boolean).join(' · ')}
                      right={formatInr(pl.price)} on={selectedPlan === pl.id} onClick={() => pickPlan(pl.id)} />
                  ))}
                  <RadioCard title="Custom" sub="Set your own days and price" on={selectedPlan === 'custom'} onClick={() => setPlanId('custom')} />
                  {otherPlans.length > 0 && (
                    <details className="more-plans">
                      <summary>Other plans ({otherPlans.length})</summary>
                      <div className="stack">
                        {otherPlans.map((pl) => (
                          <RadioCard key={pl.id} title={pl.name} sub={[`${pl.mealDays} meal days`, mealsLabel(pl.slot).toLowerCase(), pl.days ? daysLabel(pl.days) : null].filter(Boolean).join(' · ')}
                            right={formatInr(pl.price)} on={selectedPlan === pl.id} onClick={() => pickPlan(pl.id)} />
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
              <Field label="Plan name" id="f-plan-name" value={planName} onChange={(v) => { setPlanName(v); setPlanId('custom'); }} placeholder="e.g. Standard Lunch" error={fields.planName} />
              <div className="stack">
                <span className="label">Delivery days</span>
                <DayPicker label="Delivery days" value={days} onChange={(v) => { setOwnDays(v); setPlanId('custom'); }} />
              </div>
              <div className="two date-pair">
                <Field label="Meal days" id="f-days" value={mealDays} onChange={(v) => { setMealDays(v.replace(/\D/g, '')); setPlanId('custom'); }} inputMode="numeric" error={fields.mealDays} />
                <Field label="Start date" id="f-start" type="date" value={startDate} onChange={setStartDate} error={fields.startDate} />
              </div>
              <Helper>Only these days count as meal days{days.length < 7 ? ` (${daysLabel(days)})` : ''}. Holidays are skipped.</Helper>
              <Field label="Price" id="f-price" value={price} onChange={(v) => { setPrice(v.replace(/[^\d]/g, '')); setPlanId('custom'); }} prefix="₹" inputMode="numeric" error={fields.price} />
            </>
          )}
        </Card>
      </Section>

      {meals}
      {p && <Helper icon="info">Changing the start date, days or meals recalculates the end date. Phone: {formatPhone(p.subscriber.phone)}.</Helper>}

      {saveError}
      <Btn busy={save.isPending} label={save.isPending ? 'Saving…' : 'Save changes'} go="check" onClick={() => save.mutate()} disabled={!canSave} />

      {me?.user.role === 'owner' && (
        <div className="danger-zone">
          <BtnSecondary
            danger
            sm
            icon="trash"
            label={delCustomer.isPending ? 'Deleting…' : 'Delete customer'}
            busy={delCustomer.isPending}
            onClick={async () => {
              if (await confirm({ title: `Delete ${name}?`, body: 'Every plan, delivery and payment of theirs goes too, for good.', yes: 'Delete customer', danger: true })) delCustomer.mutate();
            }}
          />
          {confirmSheet}
          <ErrorText>{delCustomer.error ? errorMessage(delCustomer.error) : ''}</ErrorText>
        </div>
      )}
    </Screen>
  );
}
